'use client'

import * as z from 'zod'

import { PlusIcon } from '@radix-ui/react-icons'
import * as React from 'react'
import { useReplicacheQueryV2 } from '~/hooks-data/use-replicache-query'
import { useTeamReplicache } from '~/hooks-data/use-team-replicache'
import { useToggleState } from '~/hooks/use-toggle-state'
import { flip } from '~/lib/gsap/flip'
import { teamSpaceMutators } from '@basehub/data/replicache/mutators/team-space/mutators'
import { Button } from '~/sotano-kit/primitives/button'
import { Repository } from './repository'
import { EmptyState } from './repository/empty-state'
import ph from 'posthog-js'
import * as va from '@vercel/analytics'

import {
  Team,
  repositorySchema,
  type Repository as RepositoryType,
} from '@basehub/data/replicache/index'
import { getId } from '@basehub/utils/id/index'
import { useSelectedTeam } from '~/hooks-data/use-selected-team'
import {
  PublicRepo,
  PublicTeam,
  isPublicTeam,
  isUserTeam,
} from '@basehub/data/replicache/mutators/_public-types'
import { useUserStore } from '~/app/_store/user-store'
import { clsx } from 'clsx'
import { useIsMemberOrHigher } from '~/hooks-data/use-user-role'
import { invariant } from '@epic-web/invariant'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '~/sotano-kit/primitives/dialog'
import { Input, Label } from '~/sotano-kit/primitives/input'
import {
  RadioGroup,
  RadioItemWithLabel,
  radioClasses,
} from '~/sotano-kit/primitives/radio-group'
import { Templates } from '../dashboard'
import { useCreateRepoMutation } from '~/hooks-data/use-create-repo-mutation'
import { useDashboardSlugs } from '~/hooks/use-dashboard-slugs'
import { useRouter } from 'next/navigation'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import {
  getRepoSlugWithoutCollisions,
  getSlugFromTitle,
} from '@basehub/utils/case/index'
import { TabsStoreData } from '../../../(repo)/[repo-slug]/(editor)/explore/components/tabs/store'
import { prePopulateRepoReplicache } from '~/hooks-data/use-repo-replicache'
import { toast } from 'sonner'
import * as Form from '@radix-ui/react-form'
import LoadingIcon from '../../../(repo)/[repo-slug]/(editor)/explore/components/blocks/empty-state/loading-icon'

export const RepositoriesList = ({
  staticTeamData,
  templates,
}: {
  staticTeamData?: PublicTeam | Team
  templates: Templates['templates']['items']
}) => {
  const repositoriesRef = React.useRef<HTMLLIElement[]>([])
  const { team } = useSelectedTeam(staticTeamData)
  const { data } = useTeamReplicache()
  const optimisticUser = useUserStore((s) => s.optimisticUser)
  const newRepoDialogRef = React.useRef<NewRepositoryHandler>(null)
  const replicache = data?.replicache

  const repositoriesQuery = useReplicacheQueryV2({
    replicache,
    fetcher: (tx) => {
      invariant(data?.teamId, "No team ID, shouldn't be enabled.")
      invariant(optimisticUser?.id, "No user ID, shouldn't be enabled.")

      return teamSpaceMutators
        .listRepositories(tx, {
          teamId: data?.teamId,
          userId: optimisticUser?.id,
        })
        .catch(() => [])
    },
    key: ['repositories', data?.teamId, optimisticUser?.id],
    enabled: Boolean(data?.teamId && optimisticUser?.id),
  })

  const [newRepoCandidateTitle, setNewRepoCandidateTitle] = React.useState<
    string | null
  >(null)

  const parsedRepos = React.useMemo(() => {
    if (!repositoriesQuery.data) {
      if (isPublicTeam(team) && team.publicRepos) return team.publicRepos
      return []
    }
    return repositorySchema.array().parse(repositoriesQuery.data)
  }, [repositoriesQuery.data, team])

  // don't include the newly created repo, as dashbaord will flicker, and we're redirecting anyways.
  const filteredRepositories = React.useMemo(() => {
    return [...parsedRepos]
      .filter((r) => {
        if (!newRepoCandidateTitle) return true
        return r.title !== newRepoCandidateTitle
      })
      .sort((a, b) => {
        const lastActivity_A =
          a.lastTransferredAt ?? a.latestCommit?.createdAt ?? a.createdAt
        const lastActivity_B =
          b.lastTransferredAt ?? b.latestCommit?.createdAt ?? b.createdAt
        return (
          new Date(lastActivity_B).getTime() -
          new Date(lastActivity_A).getTime()
        )
      })
  }, [newRepoCandidateTitle, parsedRepos])

  const hasRepositories = React.useMemo(
    () => (filteredRepositories ? filteredRepositories.length > 0 : undefined),
    [filteredRepositories]
  )

  const handleRename = React.useCallback(
    async (repository: Pick<RepositoryType, 'title' | 'id'>) => {
      if (!data || !optimisticUser) throw new Error('No team data')
      await replicache?.mutate.renameRepository({
        eventId: getId(),
        isoNow: new Date().toISOString(),
        newTitle: repository.title,
        repoId: repository.id,
        teamId: data.teamId,
        userId: optimisticUser.id,
      })
    },
    [replicache?.mutate, data, optimisticUser]
  )

  const handlePrivacyToggle = React.useCallback(
    async (repository: Pick<RepositoryType, 'isPublic' | 'id'>) => {
      await replicache?.mutate.updateRepositoryPrivacy({
        isPublic: !repository.isPublic,
        repoId: repository.id,
      })
    },
    [replicache?.mutate]
  )

  const handleDelete = React.useCallback(
    (id: RepositoryType['id']) => {
      if (!data || !optimisticUser) throw new Error('No team data')
      replicache?.mutate.deleteRepository({
        repoId: id,
        teamId: data.teamId,
        eventId: getId(),
        isoNow: new Date().toISOString(),
        userId: optimisticUser.id,
      })
    },
    [data, replicache?.mutate, optimisticUser]
  )
  const isMemberOrHigher = useIsMemberOrHigher()

  return (
    <div className="flex flex-col flex-shrink-0 basis-3/5 flex-1">
      {(isPublicTeam(team) || !isMemberOrHigher) && !hasRepositories ? (
        isPublicTeam(team) ? (
          <h2 className="text-xl font-medium leading-[1.3]">
            {team?.title} doesn’t have any public repositories yet.
          </h2>
        ) : (
          <div className="font-medium">
            <h2 className="text-xl leading-[1.3]">
              There are no repositories yet
            </h2>
            <p className="text-dark-muted text-sm leading-normal mt-1">
              Once an owner or a developer creates one, you will see it here.
            </p>
          </div>
        )
      ) : (
        <>
          <div className="flex items-center justify-between mb-6">
            <h4 className="text-lg font-medium leading-5">Repositories</h4>
            {isMemberOrHigher && isUserTeam(team) && (
              <NewRepositoryButton
                ref={newRepoDialogRef}
                templates={templates}
                disabled={!repositoriesQuery.data || !isUserTeam(team)}
                repositories={filteredRepositories ?? []}
                onSubmit={(newRepoTitle) => {
                  const isInvalid = repositoriesQuery.data?.some(
                    (r) => r.title === newRepoTitle
                  )
                  if (isInvalid) {
                    setNewRepoCandidateTitle(null)
                  } else {
                    setNewRepoCandidateTitle(newRepoTitle)
                  }
                }}
              />
            )}
          </div>

          {(isPublicTeam(team) || !repositoriesQuery.isLoading) && (
            <>
              {hasRepositories ? (
                <ul className="grid w-full gap-3 lg:grid-cols-2 xl:gap-4 xl:pb-10">
                  {filteredRepositories.map((repository) => {
                    return (
                      <li
                        key={repository.id}
                        ref={(ref) => {
                          if (!repositoriesQuery.data) return
                          if (
                            !ref ||
                            repositoriesRef.current.length >
                              repositoriesQuery.data.length
                          ) {
                            return
                          }

                          repositoriesRef.current.push(ref)
                        }}
                      >
                        <Repository
                          repository={repository}
                          handleRename={handleRename}
                          handlePrivacyToggle={() =>
                            handlePrivacyToggle(repository)
                          }
                          handleDelete={() =>
                            flip(
                              repositoriesRef.current,
                              () => handleDelete(repository.id),
                              {
                                duration: 0.2,
                                ease: 'power2.inOut',
                              }
                            )
                          }
                          staticTeamData={staticTeamData}
                        />
                      </li>
                    )
                  })}
                </ul>
              ) : (
                <div className="flex flex-1 w-full h-full">
                  <EmptyState
                    templates={templates}
                    // eslint-disable-next-line react-compiler/react-compiler
                    onSelectTemplate={newRepoDialogRef.current?.open}
                  />
                </div>
              )}
            </>
          )}
        </>
      )}
    </div>
  )
}

type NewRepositoryHandler = {
  open: (template: Templates['templates']['items'][number] | null) => void
}

const NewRepositoryButton = ({
  templates,
  repositories,
  disabled,
  ref,
  onSubmit,
}: {
  templates: Templates['templates']['items']
  repositories: RepositoryType[] | PublicRepo[]
  disabled?: boolean
  ref: React.RefObject<null | NewRepositoryHandler>
  onSubmit?: (newRepoTitle: string) => void
}) => {
  const [startpoint, setStartpoint] = React.useState<string>('__blank__')
  const gridRef = React.useRef<HTMLDivElement>(null)
  const gridItemsRef = React.useRef<HTMLButtonElement[]>([])
  const repositoryFormToggleState = useToggleState()

  const inputRef = React.useRef<HTMLInputElement>(null)
  const teamReplicacheQuery = useTeamReplicache()
  const {
    mutate,
    isLoading: isLoadingCreateRepoMutation,
    isSuccess: isSuccessCreateRepoMutation,
  } = useCreateRepoMutation()
  const { teamSlug } = useDashboardSlugs()
  const optimisticUser = useUserStore((s) => s.optimisticUser)
  const router = useRouter()
  const queryClient = useQueryClient()
  const startpointTemplate = templates.find((t) => t._id === startpoint)
  const startpointIsTemplate = !!startpointTemplate

  const {
    mutate: templateMutation,
    isLoading: isLoadingTemplateMutation,
    isSuccess: isSuccessTemplateMutation,
  } = useMutation(
    async ({
      title,
      visibility,
    }: {
      title: string
      visibility: 'public' | 'private'
    }) => {
      invariant(teamSlug, 'teamSlug must be defined')
      try {
        const [templateTeamSlug, templateRepoSlug] = z
          .tuple([z.string(), z.string()])
          .parse([
            startpointTemplate?.basehubRepoUrl.split('/')[3],
            startpointTemplate?.basehubRepoUrl.split('/')[4],
          ])
        const deployUrl = await fetch(
          `/api/templates/deploy?teamSlug=${encodeURIComponent(
            templateTeamSlug
          )}&repoSlug=${encodeURIComponent(
            templateRepoSlug
          )}&teamTargetSlug=${encodeURIComponent(
            teamSlug
          )}&visibility=${visibility}&repoName=${encodeURIComponent(title)}`
        )
          .then((res) => res.json())
          .then((res) => z.object({ deployUrl: z.string() }).parse(res))
          .then(({ deployUrl }) => {
            const url = new URL(deployUrl)
            const sluglifiedTitle = getSlugFromTitle(title)
            url.searchParams.set('repository-name', sluglifiedTitle)
            url.searchParams.set('project-name', sluglifiedTitle)
            url.searchParams.set('visibility', visibility)
            return url.toString()
          })

        router.push(deployUrl)
        return
      } catch (error) {
        console.error(error)
        toast.error('Something went wrong. Please try again.')
      }
    }
  )

  const updateStartpoint = React.useCallback(
    (newStartpoint: string, opts?: { focusInput: boolean }) => {
      gridItemsRef.current = []
      setStartpoint(newStartpoint)
      if (!inputRef.current?.value && opts?.focusInput) {
        inputRef.current?.focus()
      }
    },
    [inputRef]
  )

  React.useImperativeHandle(ref, () => {
    return {
      open: (template) => {
        updateStartpoint(!template ? '__blank__' : template._id)
        repositoryFormToggleState.handleOn()
      },
    }
  }, [repositoryFormToggleState, updateStartpoint])

  const handleGridItemKeyDown: React.KeyboardEventHandler<HTMLButtonElement> = (
    e
  ) => {
    const activeItem = document.activeElement
    if (!(activeItem instanceof HTMLButtonElement)) return
    const gridItems = gridItemsRef.current
    if (!gridItems.length) return
    const activeElementIsGridItem = gridItems.includes(activeItem)
    const gridContainer = gridRef.current

    if (
      !gridContainer ||
      !activeItem ||
      !gridItems.length ||
      !activeElementIsGridItem
    ) {
      return
    }
    const activeIndex = Array.from(gridItems).indexOf(activeItem)
    const gridColumnCount = getComputedStyle(gridContainer)
      .getPropertyValue('grid-template-columns')
      .split(' ').length

    switch (e.key) {
      // case 'ArrowUp': {
      //   e.preventDefault()

      //   // Focus the item above in the same column
      //   const upIndex = activeIndex - gridColumnCount
      //   if (upIndex >= 0) {
      //     gridItems[upIndex]?.focus()
      //   }
      //   break
      // }
      // case 'ArrowDown': {
      //   e.preventDefault()

      //   // Focus the item below in the same column
      //   const downIndex = activeIndex + gridColumnCount
      //   if (downIndex < gridItems.length) {
      //     gridItems[downIndex]?.focus()
      //   }
      //   break
      // }
      case 'ArrowUp':
      case 'ArrowLeft': {
        // Focus the previous item in the same row, or the last item in the previous row if at the start of the row
        const leftIndex = activeIndex - 1
        if (leftIndex >= 0) {
          gridItems[leftIndex]?.focus()
        }
        break
      }
      case 'ArrowDown':
      case 'ArrowRight': {
        e.preventDefault()

        // Focus the next item in the same row, or the first item in the next row if at the end of the row
        const rightIndex = activeIndex + 1
        if (
          rightIndex < gridItems.length &&
          rightIndex % gridColumnCount !== 0
        ) {
          gridItems[rightIndex]?.focus()
        } else if (rightIndex < gridItems.length) {
          gridItems[rightIndex]?.focus()
        }
        break
      }
    }
  }

  const handleSubmit: React.FormEventHandler<HTMLFormElement> =
    React.useCallback(
      async (e) => {
        e.preventDefault()

        if (
          !teamReplicacheQuery.data?.replicache ||
          !teamSlug ||
          !optimisticUser?.id
        ) {
          return
        }
        const formData = new FormData(e.currentTarget)
        const title = formData.get('title') as string
        const visibility = z
          .union([z.literal('public'), z.literal('private')])
          .parse(formData.get('new-repository-visibility'))

        if (startpointIsTemplate) {
          templateMutation(
            {
              title,
              visibility,
            },
            {
              onError: (error) => {
                console.error(error)
                toast.error('Something went wrong. Please try again.')
              },
            }
          )
          return
        }

        onSubmit?.(title)
        mutate(
          {
            title,
            teamId: teamReplicacheQuery.data.teamId,
            userId: optimisticUser.id,
            template: 'blank',
            visibility,
          },
          {
            onSuccess: async ({ repo, firstBranch, templateIdSeed }) => {
              // start with the dev tab open
              sessionStorage.setItem(
                `bshb-tabs-${repo.id}`,
                JSON.stringify({
                  state: {
                    tabs: [
                      {
                        key: 'dev:connect',
                        partiallyOpen: false,
                      },
                    ],
                    activeTab: null,
                    forceActiveTabOntoURL: false,
                  } satisfies Pick<
                    TabsStoreData,
                    'tabs' | 'forceActiveTabOntoURL' | 'activeTab'
                  >,
                })
              )

              // initReplicache and populate the use-repo-replicache query
              await prePopulateRepoReplicache({
                repo,
                firstBranch,
                teamReplicacheQuery,
                queryClient,
                userId: optimisticUser.id,
                isGuest: false,
                isGuestInHisBranch: false,
                playgroundId: undefined,
              })

              va.track('Repo Created', {
                teamSlug,
                name: repo.title,
                user: optimisticUser.email,
              })
              ph.capture('Repo Created', {
                teamSlug,
                name: repo.title,
                user: optimisticUser.email,
              })

              if (templateIdSeed) {
                return router.push(
                  `/${teamSlug}/${repo.slug}/explore/main/${templateIdSeed}`
                )
              }
              router.push(`/${teamSlug}/${repo.slug}/explore/main`)
            },
            onError: (error) => {
              console.error(error)
              toast.error('Something went wrong. Please try again.')
            },
          }
        )
      },
      [
        mutate,
        onSubmit,
        optimisticUser?.email,
        optimisticUser?.id,
        queryClient,
        router,
        startpointIsTemplate,
        teamReplicacheQuery,
        teamSlug,
        templateMutation,
      ]
    )

  const isLoading = isLoadingCreateRepoMutation || isLoadingTemplateMutation
  const isSuccess = isSuccessCreateRepoMutation || isSuccessTemplateMutation
  const startpointClassName =
    'w-full border border-dark-border dark:border-dark-border-solid rounded-lg px-3 py-2 cursor-default gap-2 text-left data-[active="true"]:bg-dark-base-dark hover:border-dark-border-solid hover:dark:border-dark-elevated data-[active="true"]:border-dark-border-solid data-[active="true"]:dark:border-dark-elevated'

  const handleStartpointPointerDown = (
    e: React.MouseEvent,
    s: typeof startpoint
  ) => {
    e.preventDefault()
    updateStartpoint(s, { focusInput: true })
  }

  return (
    <Dialog
      open={repositoryFormToggleState.isOn}
      onOpenChange={(o) => {
        if (!o) {
          gridItemsRef.current = []
          repositoryFormToggleState.handleOff()
        } else {
          repositoryFormToggleState.handleOn()
        }
      }}
    >
      <DialogTrigger asChild>
        <Button icon={<PlusIcon width={16} height={16} />} disabled={disabled}>
          Create new
        </Button>
      </DialogTrigger>

      <DialogContent asChild className="!max-w-[640px]">
        <Form.Root onSubmit={handleSubmit}>
          <DialogHeader divider>
            <DialogTitle>Create a new repository</DialogTitle>
            <DialogDescription>
              Start blank or choose one of our templates.
            </DialogDescription>
          </DialogHeader>

          <Form.Field name="title" className="relative pb-3">
            <div className="flex items-baseline justify-between">
              <Form.Label className="text-xs font-medium text-dark-muted pb-1.5">
                Name your repository
              </Form.Label>

              <FormMessage
                match="tooLong"
                copy="Can be up to 3 characters long."
              />
              <FormMessage
                match="tooShort"
                copy="Must be at least 1 character long."
              />
              <FormMessage match="valueMissing" copy="Name is required." />
              <FormMessage
                match={(value) => {
                  if (!repositories) {
                    return false
                  }

                  return repositories.some((r) => r.title === value)
                }}
                copy="Name should be unique."
              />
              <FormMessage
                match={(value) => {
                  const slugCandidate = getRepoSlugWithoutCollisions(
                    '',
                    value,
                    repositories ?? []
                  )
                  return slugCandidate === null
                }}
                copy="Slug is required."
              />
            </div>

            <Form.Control asChild>
              <Input
                name="title"
                placeholder="Type here"
                ref={inputRef}
                size="sm"
                required={true}
                autoComplete="off"
                role="new-repo-name-input"
                maxLength={100}
                minLength={1}
                disabled={isLoading}
              />
            </Form.Control>
          </Form.Field>

          <Label
            size="xs"
            label="Start from scratch or pick a template"
            className="!pb-2.5 pt-3"
          >
            <div ref={gridRef} className="gap-3 grid grid-cols-3">
              <Button
                unstyled
                type="button"
                focusStyled
                ref={(ref) => {
                  if (!ref) return
                  gridItemsRef.current[0] = ref
                }}
                data-active={startpoint === '__blank__'}
                className={clsx(startpointClassName, 'col-span-full')}
                onPointerDown={(e) =>
                  handleStartpointPointerDown(e, '__blank__')
                }
                onKeyDown={(e) => {
                  handleGridItemKeyDown(e)
                  if (e.key === 'Enter') {
                    updateStartpoint('__blank__')
                  }
                }}
              >
                <div className="w-4 flex-1 contents">
                  <div className="flex justify-between items-start mb-1">
                    <p className="font-medium text-xs text-dark-bright">
                      Blank repo
                    </p>

                    {/* fake radio */}
                    <span
                      className={clsx('-mr-1 shrink-0', radioClasses.item)}
                      data-state={
                        startpoint === '__blank__' ? 'checked' : 'unchecked'
                      }
                    >
                      {startpoint === '__blank__' && (
                        <span className={radioClasses.indicator} />
                      )}
                    </span>
                  </div>
                  <p className="text-dark-faint text-xs">
                    Start with a clean slate for total customization.
                  </p>
                </div>
              </Button>

              {templates.map((template, index) => {
                const isSelected = startpoint === template._id

                return (
                  <Button
                    key={template._id}
                    unstyled
                    type="button"
                    focusStyled
                    data-active={isSelected}
                    className={clsx(
                      startpointClassName,
                      'flex flex-col gap-y-0'
                    )}
                    onKeyDown={(e) => {
                      handleGridItemKeyDown(e)
                      if (e.key === 'Enter') {
                        updateStartpoint(template._id)
                      }
                    }}
                    tabIndex={
                      (!startpointIsTemplate && index === 0) || isSelected
                        ? 0
                        : -1
                    }
                    ref={(ref) => {
                      if (
                        ref &&
                        gridItemsRef.current.length < templates.length + 1
                      ) {
                        gridItemsRef.current.push(ref)
                      }
                    }}
                    onPointerDown={(e) =>
                      handleStartpointPointerDown(e, template._id)
                    }
                  >
                    <div className="flex justify-between w-full items-start mb-1">
                      <p className="font-medium text-xs text-dark-bright">
                        {template._title}
                      </p>

                      {/* fake radio */}
                      <span
                        className={clsx('-mr-1 shrink-0', radioClasses.item)}
                        data-state={isSelected ? 'checked' : 'unchecked'}
                      >
                        {isSelected && (
                          <span className={radioClasses.indicator} />
                        )}
                      </span>
                    </div>
                    <p className="text-dark-faint text-xs text-pretty">
                      {template.shortDescription}
                    </p>
                  </Button>
                )
              })}
            </div>
          </Label>

          <Label label="Visibility" className="pt-3 pb-0" size="xs">
            <RadioGroup name="new-repository-visibility" defaultValue="private">
              <RadioItemWithLabel value="private">Private</RadioItemWithLabel>
              <RadioItemWithLabel value="public">Public</RadioItemWithLabel>
            </RadioGroup>
          </Label>

          <DialogFooter divider>
            <DialogClose asChild>
              <Button intent="secondary">Cancel</Button>
            </DialogClose>
            <Form.Submit asChild>
              <Button
                size="small"
                intent="primary"
                type="submit"
                disabled={isLoading || isSuccess}
                icon={
                  (isLoading || isSuccess) && (
                    <LoadingIcon
                      className="!text-white"
                      status={isLoading ? 'loading' : 'idle'}
                    />
                  )
                }
              >
                {isLoading
                  ? 'Creating...'
                  : isSuccess
                    ? 'Redirecting...'
                    : 'Create'}
              </Button>
            </Form.Submit>
          </DialogFooter>
        </Form.Root>
      </DialogContent>
    </Dialog>
  )
}

export const FormMessage = ({
  copy,
  ...rest
}: { copy: React.ReactNode } & Form.FormMessageProps) => {
  return (
    <Form.Message
      {...rest}
      className="text-xs font-medium flex items-center gap-1.5 rounded-md leading-none text-red"
    >
      {copy}
    </Form.Message>
  )
}
